import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    RiDashboardLine,
    RiPlantLine,
    RiNewspaperLine,
    RiBookOpenLine,
    RiMapPinLine,
    RiUserLine,
    RiTimeLine,
    RiArrowRightSLine,
    RiLoader4Line
} from 'react-icons/ri';
import { API_BASE_URL } from '../../config';

const DashboardCard = ({ label, value, icon: Icon, colorClass, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay }}
        className="bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 group hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500"
    >
        <div className="flex justify-between items-start mb-4">
            <div className={`p-3 rounded-2xl ${colorClass} transition-transform group-hover:scale-110 duration-500`}>
                <Icon size={24} />
            </div>
            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest font-sans">Live Metric</span>
        </div>
        <div>
            <span className="text-slate-400 text-xs font-bold uppercase tracking-widest font-sans">{label}</span>
            <p className="text-4xl font-black text-slate-900 mt-2 font-display">{value}</p>
        </div>
    </motion.div>
);

const Dashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/dashboard/stats.php`);
                const result = await response.json();
                if (response.ok) {
                    setData(result);
                }
            } catch (error) {
                console.error('Error fetching dashboard stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="min-h-[400px] flex items-center justify-center">
                <RiLoader4Line className="animate-spin text-emerald-500 text-4xl" />
            </div>
        );
    }

    const { stats, recent_news } = data || { stats: {}, recent_news: [] };

    return (
        <div className="max-w-6xl">
            <header className="mb-12">
                <div className="flex items-center gap-3 text-emerald-600 mb-2">
                    <RiDashboardLine size={20} />
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] font-sans">Command Center</span>
                </div>
                <h2 className="text-4xl font-bold text-slate-900 font-display">Dashboard Overview</h2>
                <p className="text-slate-500 mt-2 font-sans">Real-time telemetry and management controls for the COE ecosystem.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                <DashboardCard
                    label="Biodiversity Records"
                    value={stats.species || 0}
                    icon={RiPlantLine}
                    colorClass="bg-emerald-50 text-emerald-600"
                    delay={0.1}
                />
                <DashboardCard
                    label="Published News"
                    value={stats.news || 0}
                    icon={RiNewspaperLine}
                    colorClass="bg-blue-50 text-blue-600"
                    delay={0.2}
                />
                <DashboardCard
                    label="Research Papers"
                    value={stats.publications || 0}
                    icon={RiBookOpenLine}
                    colorClass="bg-purple-50 text-purple-600"
                    delay={0.3}
                />
                <DashboardCard
                    label="Travel Destinations"
                    value={stats.destinations || 0}
                    icon={RiMapPinLine}
                    colorClass="bg-amber-50 text-amber-600"
                    delay={0.4}
                />
                <DashboardCard
                    label="Administrative Staff"
                    value={stats.users || 0}
                    icon={RiUserLine}
                    colorClass="bg-slate-50 text-slate-600"
                    delay={0.5}
                />
                <DashboardCard
                    label="Total Community Impact"
                    value={stats.total_impact || 0}
                    icon={RiDashboardLine}
                    colorClass="bg-emerald-50 text-emerald-600"
                    delay={0.7}
                />
                <DashboardCard
                    label="Pending Feedback"
                    value={stats.pending_feedback || 0}
                    icon={RiTimeLine}
                    colorClass="bg-rose-50 text-rose-600"
                    delay={0.6}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Activity */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white rounded-[32px] border border-slate-100 p-8 shadow-sm"
                >
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="text-lg font-bold text-slate-900 font-display flex items-center gap-2">
                            <RiTimeLine className="text-emerald-500" />
                            Recent Transmission
                        </h3>
                    </div>

                    <div className="space-y-6">
                        {recent_news.map((item, i) => (
                            <div key={i} className="flex gap-4 items-start group cursor-pointer hover:bg-slate-50 p-2 rounded-xl transition-colors">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-slate-800 group-hover:text-emerald-600 transition-colors line-clamp-1 font-sans">
                                        {item.title}
                                    </h4>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1 font-sans">
                                        {new Date(item.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <RiArrowRightSLine className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Quick Actions / Tips */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-slate-900 rounded-[32px] p-8 text-white relative overflow-hidden"
                >
                    <div className="relative z-10">
                        <h3 className="text-lg font-bold mb-4 font-display">System Integrity</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-8 font-sans">
                            All systems are currently operational. Database synchronization is active, and satellite feeds are processing regional data.
                        </p>

                        <div className="space-y-4">
                            <div className="bg-white/10 p-4 rounded-2xl flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                                    <RiPlantLine size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-bold font-sans">Data Synchronization</p>
                                    <p className="text-[10px] text-slate-500 font-sans">100% Verified</p>
                                </div>
                            </div>
                            <div className="bg-white/10 p-4 rounded-2xl flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                                    <RiUserLine size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-bold font-sans">Active Sessions</p>
                                    <p className="text-[10px] text-slate-500 font-sans">3 Admin Online</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Decorative element */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                </motion.div>
            </div>
        </div>
    );
};

export default Dashboard;
